import React, { useCallback, useEffect, useState } from 'react';

import './saved.css';
import './admin.css';
import {
  BottomNav,
  ColorRail,
  LockIcon,
  SELLER_NAV,
  ShieldTrustIcon,
  TopBar,
} from '../../../commodity/components/caria';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';
import {
  fetchModerationQueue,
  ModerationQueueEntry,
  reinstateListing,
  suspendListing,
} from '../api/moderation.api';

/**
 * AdminModeration (T0 Epic D.2) — ADMIN/MODERATOR queue. Lists reported or
 * suspended listings and exposes suspend / reinstate actions.
 *
 * Ported onto the CAR-IA chrome with the prototype's segmented control and
 * row-card list. It previously rendered inside the pre-lime forest-green
 * MainLayout, which below 768px opened a full-viewport drawer over the queue.
 */
type Tab = 'REPORTED' | 'SUSPENDED';

export const AdminModeration: React.FC = () => {
  const authChrome = useAuthChrome();
  const [tab, setTab] = useState<Tab>('REPORTED');
  const [entries, setEntries] = useState<ModerationQueueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState<string | undefined>();

  const load = useCallback(async (state: Tab) => {
    setLoading(true);
    setError(undefined);
    setForbidden(false);
    try {
      setEntries(await fetchModerationQueue(state));
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      // A non-admin gets 403 here; that is authorization working, so show an
      // explanatory Spanish state rather than the raw API string.
      if (/insufficient permissions|forbidden|\b403\b/i.test(msg)) {
        setForbidden(true);
      } else {
        setError(msg || 'No se pudo cargar la cola.');
      }
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const onSuspend = async (vehicleId: string) => {
    const reason = window.prompt('Motivo de la suspensión:');
    if (!reason) return;
    setBusy(vehicleId);
    try {
      await suspendListing(vehicleId, reason);
      await load(tab);
    } finally {
      setBusy(undefined);
    }
  };

  const onReinstate = async (vehicleId: string) => {
    setBusy(vehicleId);
    try {
      await reinstateListing(vehicleId);
      await load(tab);
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <div className="cia-saved" data-testid="admin-moderation">
      <TopBar {...authChrome} nav={SELLER_NAV} />
      <ColorRail />

      <div className="cia-saved-wrap">
        <div className="cia-saved-head">
          <h1>Moderación</h1>
          {!loading && !error && !forbidden && entries.length > 0 && (
            <span className="cia-saved-count">{entries.length}</span>
          )}
        </div>
        <p className="cia-saved-sub">Anuncios reportados por compradores y anuncios suspendidos.</p>

        <div className="cia-adm-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'REPORTED'}
            className={tab === 'REPORTED' ? 'on' : undefined}
            onClick={() => setTab('REPORTED')}
          >
            Reportados
          </button>
          <button
            role="tab"
            aria-selected={tab === 'SUSPENDED'}
            className={tab === 'SUSPENDED' ? 'on' : undefined}
            onClick={() => setTab('SUSPENDED')}
          >
            Suspendidos
          </button>
        </div>

        {loading && (
          <div className="cia-saved-state" aria-busy="true">
            <p>Cargando…</p>
          </div>
        )}

        {!loading && forbidden && (
          <div className="cia-saved-state" data-testid="admin-moderation-forbidden">
            <span className="cia-saved-state-ic">
              <LockIcon size={25} strokeWidth={1.8} />
            </span>
            <h2>Necesitas permisos de moderación</h2>
            <p>Esta cola solo está disponible para cuentas de administrador o moderador.</p>
          </div>
        )}

        {!loading && error && (
          <div className="cia-saved-state">
            <h2>No pudimos cargar la cola</h2>
            <p role="alert">{error}</p>
          </div>
        )}

        {!loading && !error && !forbidden && entries.length === 0 && (
          <div className="cia-saved-state" data-testid="admin-moderation-empty">
            <span className="cia-saved-state-ic">
              <ShieldTrustIcon size={25} strokeWidth={1.8} />
            </span>
            <h2>Cola vacía</h2>
            <p>
              {tab === 'REPORTED'
                ? 'No hay anuncios reportados pendientes de revisión.'
                : 'No hay anuncios suspendidos.'}
            </p>
          </div>
        )}

        {!loading && !error && !forbidden && entries.length > 0 && (
          <div className="cia-saved-list">
            {entries.map((e) => (
              <div key={e.vehicleId} className="cia-saved-row">
                <span className="cia-saved-info">
                  <span className="cia-saved-title">{e.title}</span>
                  <span className="cia-saved-meta">
                    {tab === 'REPORTED'
                      ? `${e.reportCount} reporte(s) · ${e.reasons.join(', ')}`
                      : e.suspensionReason || 'Sin motivo registrado'}
                  </span>
                </span>
                <button
                  className={`cia-adm-action${tab === 'REPORTED' ? ' danger' : ''}`}
                  disabled={busy === e.vehicleId}
                  onClick={() =>
                    tab === 'REPORTED' ? onSuspend(e.vehicleId) : onReinstate(e.vehicleId)
                  }
                >
                  {tab === 'REPORTED' ? 'Suspender' : 'Reactivar'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default AdminModeration;
